<script setup>
import { ref, computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const emit = defineEmits(['import']);
const { t } = useI18n();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isImportingContact = computed(() => uiFlags.value.isImporting);
const accountLabels = useMapGetter('labels/getLabels');

const labelMenuItems = computed(() =>
  (accountLabels.value || []).map(label => ({
    action: label.title,
    value: label.title,
    label: label.title,
  }))
);

const dialogRef = ref(null);
const fileInput = ref(null);

const hasSelectedFile = ref(null);
const selectedFileName = ref('');
const selectedTags = ref([]);
const duplicateStrategy = ref('update_existing');

// Two-step state
const currentStep = ref('upload'); // 'upload' | 'mapping'
const isLoadingPreview = ref(false);
const previewHeaders = ref([]);
const previewRows = ref([]);
const previewTotalRows = ref(null);
const targetFields = ref([]);
const columnMapping = ref({});

const csvUrl = '/downloads/import-contacts-sample.csv';

const ACCEPTED_TYPES =
  'text/csv,.csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

const isScientificNotation = value => {
  if (!value) return false;
  const str = String(value);
  return (str.includes('E+') || str.includes('e+')) && /\d/.test(str);
};

const handleFileClick = () => fileInput.value?.click();

const processFileName = fileName => {
  const lastDotIndex = fileName.lastIndexOf('.');
  const extension = fileName.slice(lastDotIndex);
  const baseName = fileName.slice(0, lastDotIndex);

  return baseName.length > 20
    ? `${baseName.slice(0, 20)}...${extension}`
    : fileName;
};

const handleFileChange = () => {
  const file = fileInput.value?.files[0];
  hasSelectedFile.value = file;
  selectedFileName.value = file ? processFileName(file.name) : '';

  if (file && selectedTags.value.length === 0) {
    const now = new Date();
    const options = {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };
    const dateStr = now.toLocaleDateString('pt-BR', options).replace(',', '');
    const defaultTag = `${t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')} ${dateStr}`;
    selectedTags.value = [defaultTag];
  }

  // Reset mapping state when file changes, but keep tags and strategy
  currentStep.value = 'upload';
  previewHeaders.value = [];
  previewRows.value = [];
  columnMapping.value = {};
};

const handleRemoveFile = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  selectedFileName.value = '';
  currentStep.value = 'upload';
  previewHeaders.value = [];
  previewRows.value = [];
  columnMapping.value = {};
};

const autoDetectMapping = headers => {
  const mapping = {};
  const normalizeKey = key => key.toLowerCase().replace(/[^a-z0-9]/g, '');

  const knownMappings = {
    name: ['name', 'nome', 'fullname', 'full_name', 'contactname', 'contact'],
    email: ['email', 'emailaddress', 'email_address', 'mail', 'correio'],
    phone_number: [
      'phone_number',
      'phonenumber',
      'phone',
      'telefone',
      'tel',
      'celular',
      'whatsapp',
      'mobile',
      'cell',
    ],
    identifier: ['identifier', 'id', 'external_id', 'externalid'],
    company: ['company', 'empresa', 'companyname', 'company_name', 'org'],
    city: ['city', 'cidade', 'town'],
  };

  headers.forEach(header => {
    const normalizedHeader = normalizeKey(header);
    Object.entries(knownMappings).forEach(([field, aliases]) => {
      if (aliases.includes(normalizedHeader) && !mapping[header]) {
        mapping[header] = field;
      }
    });
  });

  return mapping;
};

const loadPreview = async () => {
  if (!hasSelectedFile.value) return;
  isLoadingPreview.value = true;

  try {
    const response = await ContactAPI.previewImport(hasSelectedFile.value);
    const {
      headers,
      rows,
      total_rows: totalRows,
      target_fields: fields,
    } = response.data;

    previewHeaders.value = headers;
    previewRows.value = rows;
    previewTotalRows.value = totalRows;
    targetFields.value = fields;
    columnMapping.value = autoDetectMapping(headers);
    currentStep.value = 'mapping';
  } catch {
    currentStep.value = 'upload';
  } finally {
    isLoadingPreview.value = false;
  }
};

const confirmButtonLabel = computed(() => {
  if (currentStep.value === 'upload') {
    return t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.NEXT_STEP');
  }
  return t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.IMPORT');
});

const isLoading = computed(
  () => isImportingContact.value || isLoadingPreview.value
);

const uploadFile = async () => {
  if (!hasSelectedFile.value) return;

  // Build the final mapping (only include mapped fields)
  const finalMapping = {};
  Object.entries(columnMapping.value).forEach(([source, target]) => {
    if (target) {
      finalMapping[source] = target;
    }
  });

  emit('import', {
    file: hasSelectedFile.value,
    columnMapping: Object.keys(finalMapping).length > 0 ? finalMapping : null,
    tags: selectedTags.value,
    duplicateStrategy: duplicateStrategy.value,
  });
};

const handleConfirm = () => {
  if (currentStep.value === 'upload') {
    loadPreview();
  } else {
    uploadFile();
  }
};

const goBackToUpload = () => {
  currentStep.value = 'upload';
};

const resetState = () => {
  hasSelectedFile.value = null;
  if (fileInput.value) fileInput.value.value = null;
  selectedFileName.value = '';
  selectedTags.value = [];
  duplicateStrategy.value = 'update_existing';
  currentStep.value = 'upload';
  previewHeaders.value = [];
  previewRows.value = [];
  columnMapping.value = {};
};

defineExpose({ dialogRef, resetState });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TITLE')"
    :confirm-button-label="confirmButtonLabel"
    :is-loading="isLoading"
    :disable-confirm-button="isLoading || !hasSelectedFile"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetState"
  >
    <template #description>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DESCRIPTION') }}
        <a
          :href="csvUrl"
          target="_blank"
          rel="noopener noreferrer"
          download="import-contacts-sample.csv"
          class="text-n-blue-11"
        >
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DOWNLOAD_LABEL')
          }}
        </a>
      </p>
    </template>

    <!-- Step 1: File Upload -->
    <div v-if="currentStep === 'upload'" class="flex flex-col gap-4">
      <div class="flex items-center gap-2">
        <label class="min-w-[120px] text-sm text-n-slate-12 whitespace-nowrap">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.LABEL') }}
        </label>
        <div class="flex items-center justify-between w-full gap-2">
          <span v-if="hasSelectedFile" class="text-sm text-n-slate-12">
            {{ selectedFileName }}
          </span>
          <Button
            v-if="!hasSelectedFile"
            :label="
              t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHOOSE_FILE')
            "
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveFile"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Step 2: Field Mapping + Tags + Duplicate strategy -->
    <div v-if="currentStep === 'mapping'" class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <Button
          :label="t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.BACK')"
          icon="i-lucide-arrow-left"
          color="slate"
          variant="ghost"
          size="sm"
          @click="goBackToUpload"
        />
        <span v-if="previewTotalRows" class="text-xs text-n-slate-11">
          {{
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TOTAL_ROWS', {
              count: previewTotalRows,
            })
          }}
        </span>
      </div>

      <!-- Mapping Table -->
      <div class="rounded-lg border border-n-weak overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-n-alpha-1">
              <th
                class="px-3 py-2 text-left text-xs font-medium text-n-slate-11"
              >
                {{
                  t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.FILE_COLUMN')
                }}
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-n-slate-11"
              >
                {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.MAPS_TO') }}
              </th>
              <th
                class="px-3 py-2 text-left text-xs font-medium text-n-slate-11"
              >
                {{
                  t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SAMPLE_DATA')
                }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="header in previewHeaders"
              :key="header"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2 text-n-slate-12 font-medium">
                {{ header }}
              </td>
              <td class="px-3 py-2">
                <select
                  v-model="columnMapping[header]"
                  class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
                >
                  <option value="">
                    {{
                      t(
                        'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SKIP_COLUMN'
                      )
                    }}
                  </option>
                  <option
                    v-for="field in targetFields"
                    :key="field.key"
                    :value="field.key"
                  >
                    {{ field.label }}
                  </option>
                </select>
              </td>
              <td
                class="px-3 py-2 text-n-slate-11 text-xs truncate max-w-[120px]"
              >
                <div class="flex flex-col gap-1">
                  <span>{{ previewRows[0]?.[header] || '—' }}</span>
                  <span
                    v-if="isScientificNotation(previewRows[0]?.[header])"
                    class="text-[10px] text-n-ruby-11 font-medium leading-tight"
                  >
                    {{
                      t(
                        'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.SCIENTIFIC_NOTATION_WARNING',
                        'Excel scientific notation detected. Please format as text.'
                      )
                    }}
                  </span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Tags Section -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TAGS_LABEL') }}
        </label>
        <TagInput
          v-model="selectedTags"
          :menu-items="labelMenuItems"
          allow-create
          show-dropdown
          mode="multiple"
          class="w-full bg-n-alpha-1"
          :placeholder="
            t('CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.TAGS_PLACEHOLDER')
          "
        />
      </div>

      <!-- Duplicate Strategy -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{
            t(
              'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DUPLICATE_STRATEGY.LABEL'
            )
          }}
        </label>
        <div class="flex flex-col gap-2">
          <label
            class="flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors"
            :class="
              duplicateStrategy === 'update_existing'
                ? 'border-n-brand bg-n-alpha-1'
                : 'border-n-weak hover:border-n-slate-7'
            "
          >
            <input
              v-model="duplicateStrategy"
              type="radio"
              value="update_existing"
              class="mt-0.5 accent-n-brand"
            />
            <div>
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DUPLICATE_STRATEGY.UPDATE_EXISTING'
                  )
                }}
              </span>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{
                  t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DUPLICATE_STRATEGY.UPDATE_EXISTING_DESC'
                  )
                }}
              </p>
            </div>
          </label>
          <label
            class="flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition-colors"
            :class="
              duplicateStrategy === 'skip_duplicates'
                ? 'border-n-brand bg-n-alpha-1'
                : 'border-n-weak hover:border-n-slate-7'
            "
          >
            <input
              v-model="duplicateStrategy"
              type="radio"
              value="skip_duplicates"
              class="mt-0.5 accent-n-brand"
            />
            <div>
              <span class="text-sm font-medium text-n-slate-12">
                {{
                  t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DUPLICATE_STRATEGY.SKIP_DUPLICATES'
                  )
                }}
              </span>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{
                  t(
                    'CONTACTS_LAYOUT.HEADER.ACTIONS.IMPORT_CONTACT.DUPLICATE_STRATEGY.SKIP_DUPLICATES_DESC'
                  )
                }}
              </p>
            </div>
          </label>
        </div>
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      :accept="ACCEPTED_TYPES"
      class="hidden"
      @change="handleFileChange"
    />
  </Dialog>
</template>
